<script setup lang="ts">
import { nextTick, ref, watch } from "vue";

interface SelectedPurchaseOrderView {
  poNumber: string;
  estArrival: string;
}

// selectionNoun/itemsLabel/emptyHint let the Intermediate Order grid reuse this
// panel with its own wording. Defaults reproduce the PO Recommendations copy.
const properties = withDefaults(
  defineProps<{
    selectedPos: SelectedPurchaseOrderView[];
    totalSelectedItems: number;
    selectionNoun?: string;
    itemsLabel?: string;
    emptyHint?: string;
  }>(),
  {
    selectionNoun: "PO",
    itemsLabel: "items",
    emptyHint: "Select at least 2 Purchase Orders to run redistribution.",
  }
);

const emit = defineEmits<{
  removePo: [poNumber: string];
}>();

const selectionChipListRef = ref<HTMLElement | null>(null);

watch(
  () => properties.selectedPos,
  (nextSelections, previousSelections) => {
    if (nextSelections.length <= previousSelections.length) {
      return;
    }

    void nextTick(() => {
      const chipList = selectionChipListRef.value;
      if (!chipList) {
        return;
      }

      if (typeof chipList.scrollTo === "function") {
        chipList.scrollTo({ top: chipList.scrollHeight, behavior: "smooth" });
        return;
      }

      chipList.scrollTop = chipList.scrollHeight;
    });
  }
);
</script>

<template>
  <div class="min-w-0 flex-1">
    <span class="text-sm font-semibold text-[#1e3f73]">
      {{
        selectedPos.length > 0
          ? `${selectedPos.length} ${selectionNoun}(s) selected (${totalSelectedItems.toLocaleString()} ${itemsLabel})`
          : `No ${selectionNoun}s selected`
      }}
    </span>

    <p v-if="selectedPos.length === 0" class="mt-1 text-sm text-slate-600">
      {{ emptyHint }}
    </p>

    <div
      v-else
      ref="selectionChipListRef"
      class="results-panel-selection-chip-list"
    >
      <span
        v-for="po in selectedPos"
        :key="po.poNumber"
        class="results-panel-selection-chip"
      >
        {{ po.poNumber }}
        <span class="font-normal text-slate-500">({{ po.estArrival }})</span>
        <button
          class="results-panel-chip-remove"
          type="button"
          @click="emit('removePo', po.poNumber)"
        >
          ×
        </button>
      </span>
    </div>
  </div>
</template>
